// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Ask Helios service.
 *
 * Calls the BFF's structured advisory endpoint (POST /api/v1/ai/ask-helios).
 * Each turn is a single request/response rather than a token stream: the agent
 * returns a complete envelope (answer text, confidence, route, citations,
 * abstained, disclaimer) which the UI renders with confidence/citation chrome.
 * Uses the shared api.ts wrapper (auth, 401 handling, JSON parsing).
 */

import { get, patch, post, del, lockIfAccountDisabled } from './api';
import { getApiUrl, ENDPOINTS } from '../utils/constants';
import { getTokenAsync } from '../config/authConfig';
import { removeUserData } from './tokenStorage';
import { ASK_HELIOS_IDLE_TIMEOUT_MS } from '../config/queryConfig';
import { useSessionGuardStore } from '../stores/sessionGuardStore';
import type {
  AskHeliosRequestPayload,
  AskHeliosResponse,
  AskHeliosStreamEvent,
  ConversationDetail,
  ConversationListResponse,
  ConversationStatus,
  ConversationSummary,
} from '../types';

export async function askHelios(payload: AskHeliosRequestPayload): Promise<AskHeliosResponse> {
  return post<AskHeliosResponse>(ENDPOINTS.AI_ASK_HELIOS, payload);
}

// ---------------------------------------------------------------------------
// Conversation history (ADR-0009)
//
// Owner-scoped server-side: these return only the caller's own conversations,
// and a conversation belonging to someone else reads as 404 rather than 403.
// ---------------------------------------------------------------------------

export interface ListConversationsParams {
  scope?: string;
  contextRef?: string;
  status?: ConversationStatus;
  limit?: number;
  cursor?: string;
}

export async function listConversations(
  params: ListConversationsParams = {}
): Promise<ConversationListResponse> {
  const query = new URLSearchParams();
  if (params.scope) query.set('scope', params.scope);
  if (params.contextRef) query.set('context_ref', params.contextRef);
  if (params.status) query.set('status', params.status);
  if (params.limit !== undefined) query.set('limit', String(params.limit));
  if (params.cursor) query.set('cursor', params.cursor);

  const qs = query.toString();
  return get<ConversationListResponse>(`${ENDPOINTS.AI_CONVERSATIONS}${qs ? `?${qs}` : ''}`);
}

/**
 * Fetch a conversation and its stored turns — resume and replay.
 *
 * The turns carry the envelope as it was originally rendered (confidence,
 * citations, chips, disclaimer), so a replay shows what the operator saw rather
 * than re-deriving it.
 */
export async function getConversation(
  conversationId: string,
  options: { afterSeq?: number; limit?: number } = {}
): Promise<ConversationDetail> {
  const query = new URLSearchParams();
  if (options.afterSeq !== undefined) query.set('after_seq', String(options.afterSeq));
  if (options.limit !== undefined) query.set('limit', String(options.limit));

  const qs = query.toString();
  return get<ConversationDetail>(
    `${ENDPOINTS.AI_CONVERSATIONS}/${encodeURIComponent(conversationId)}${qs ? `?${qs}` : ''}`
  );
}

export async function renameConversation(
  conversationId: string,
  title: string
): Promise<ConversationSummary> {
  return patch<ConversationSummary>(
    `${ENDPOINTS.AI_CONVERSATIONS}/${encodeURIComponent(conversationId)}`,
    { title }
  );
}

/** Soft-delete: archives the conversation and pins its retention TTL. */
export async function archiveConversation(conversationId: string): Promise<ConversationSummary> {
  return del<ConversationSummary>(
    `${ENDPOINTS.AI_CONVERSATIONS}/${encodeURIComponent(conversationId)}`
  );
}

/** The one stream error the UI shows verbatim; other `onError` strings are technical. */
export const STREAM_STALLED_MESSAGE = 'Ask EnergyFluo stopped responding.';

const IDLE_TIMEOUT = Symbol('idle-timeout');

/** One `reader.read()` that rejects with `IDLE_TIMEOUT` if nothing arrives in time. */
function readWithIdleTimeout(reader: ReadableStreamDefaultReader<Uint8Array>) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const idle = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(IDLE_TIMEOUT), ASK_HELIOS_IDLE_TIMEOUT_MS);
  });
  return Promise.race([reader.read(), idle]).finally(() => clearTimeout(timer));
}

/**
 * Stream an Ask Helios answer over SSE (POST /ai/ask-helios/stream).
 *
 * Same envelope as `askHelios`, plus sanitized `progress` events emitted
 * while the orchestrator's tools run (e.g. "Searching the SOP library…") so
 * the UI isn't stuck on a static "thinking" message for the whole request.
 *
 * @param onProgress  - Called with each sanitized progress label
 * @param onEnvelope  - Called once with the final answer envelope. Its
 *                      `persisted` is `null` while the background Cosmos
 *                      write is still pending.
 * @param onError     - Called if the stream errors (network, model error, or idle timeout)
 * @param signal       - Abort signal for the fetch
 * @param onPersisted - Called with the trailing persisted-only event's value,
 *                      once the background write resolves. May never fire.
 */
export async function streamAskHelios(
  payload: AskHeliosRequestPayload,
  onProgress: (_label: string) => void,
  onEnvelope: (_envelope: AskHeliosResponse) => void,
  onError: (_error: string) => void,
  signal?: AbortSignal,
  onPersisted?: (_persisted: boolean) => void
): Promise<void> {
  const url = getApiUrl(ENDPOINTS.AI_ASK_HELIOS_STREAM);

  let token: string;
  try {
    token = await getTokenAsync();
  } catch {
    onError('Not authenticated. Please try again.');
    return;
  }

  const controller = new AbortController();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', () => controller.abort());
  let headerTimedOut = false;
  const headerTimer = setTimeout(() => {
    headerTimedOut = true;
    controller.abort();
  }, ASK_HELIOS_IDLE_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err) {
    if (signal?.aborted) return;
    if (headerTimedOut) {
      onError(STREAM_STALLED_MESSAGE);
      return;
    }
    if ((err as Error).name === 'AbortError') return;
    onError('Failed to connect to the AI service.');
    return;
  } finally {
    clearTimeout(headerTimer);
  }

  if (response.status === 401) {
    removeUserData();
    useSessionGuardStore.getState().lock('unauthorized');
    return;
  }

  await lockIfAccountDisabled(response);

  if (!response.ok) {
    onError(`AI service error (${response.status})`);
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    onError('Streaming not supported by browser.');
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await readWithIdleTimeout(reader);
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith('data: ')) continue;

        const data = trimmed.slice(6);
        if (data === '[DONE]') return;

        try {
          const event: AskHeliosStreamEvent = JSON.parse(data);
          if (event.error) {
            onError(event.error);
          } else if (event.progress) {
            onProgress(event.progress);
          } else if (event.text !== undefined) {
            onEnvelope(event as AskHeliosResponse);
          } else if (typeof event.persisted === 'boolean') {
            onPersisted?.(event.persisted);
          }
        } catch {
          // Skip malformed SSE lines
        }
      }
    }
  } catch (err) {
    if (err === IDLE_TIMEOUT) {
      void reader.cancel().catch(() => {});
      onError(STREAM_STALLED_MESSAGE);
      return;
    }
    if ((err as Error).name === 'AbortError') return;
    onError('Connection to AI service lost.');
  }
}
